import {
  EditTripFormData,
  GetTripDetailsResponse,
  QueryKeys,
  apiInstance,
  appendCommentIfDateHasChanged,
  toTripId,
  useLocale,
} from '@multiplatform-apps/service-trips';
import { useFeedbackModal } from '../context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ErrorActionParams,
  SuccessActionParams,
  UseEditTripParams,
  UseEditTripReturn,
} from './types';

/**
 * Normalizes trip address by removing null values (converts to undefined)
 */
const normalizeTripAddress = (address: Record<string, unknown> | undefined) => {
  if (!address) return {};
  return Object.fromEntries(
    Object.entries(address).filter(([, value]) => value !== null)
  ) as Record<string, unknown>;
};

/**
 * Custom hook to manage edit trip mutation and submission flow.
 * Handles confirmation modals, success/error feedback.
 * Encapsulates all trip editing business logic.
 * Fixes R-15 (god component) by extracting mutation handling.
 */
export const useEditTrip = ({
  tripData,
  onSuccess,
  renderSuccessActions,
  renderErrorActions,
}: UseEditTripParams): UseEditTripReturn => {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const feedbackModal = useFeedbackModal();

  const handleGoBackToTrips = useCallback(() => {
    feedbackModal.hide();
    navigate('/trips');
  }, [feedbackModal, navigate]);

  const handleCloseSuccessModal = useCallback(() => {
    feedbackModal.hide();
    onSuccess?.();
  }, [feedbackModal, onSuccess]);

  const handleCloseErrorModal = useCallback(() => {
    feedbackModal.hide();
  }, [feedbackModal]);

  const successActions = renderSuccessActions({
    onGoBackToTrips: handleGoBackToTrips,
    onClose: handleCloseSuccessModal,
  });

  const errorActions = renderErrorActions({
    onClose: handleCloseErrorModal,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (values: EditTripFormData) => {
      const pickupTime = values.timeType === 'pickup' ? values.time : undefined;
      const dropOffTime =
        values.timeType === 'destination' ? values.time : undefined;

      return apiInstance.updateTrip(
        toTripId(tripData.id),
        {
          commentOperator: appendCommentIfDateHasChanged(
            { type: values.timeType, date: new Date(values.time) },
            tripData.pickUpDateTime,
            tripData.dropOffTimeDateTime,
            values.otherInformation ?? ''
          ),
          fromAddress: normalizeTripAddress(values.pickupAddress?.value),
          toAddress: normalizeTripAddress(values.destinationAddress?.value),
          pickupTime,
          dropOffTime,
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Trip] });
      feedbackModal.show({
        type: 'success',
        title: t('trips.details.edit.trip.confirmModal.success.message'),
        actions: successActions,
        onClose: handleCloseSuccessModal,
      });
    },
    onError: () => {
      feedbackModal.show({
        type: 'error',
        title: t('trips.details.edit.trip.confirmModal.error.message'),
        actions: errorActions,
        onClose: handleCloseErrorModal,
      });
    },
  });

  const submitTrip = useCallback(
    async (values: EditTripFormData) => {
      try {
        await mutateAsync(values);
      } catch {
        // Error handled by onError in mutation
      }
    },
    [mutateAsync]
  );

  return {
    submitTrip,
    isPending,
  };
};
